import { fromShiftApi, shiftRequest } from "../ShiftEarnings/shiftEarningsApi";

// Payroll > Pay Inputs > Leave talks to the Leave Earning APIs
// (TblPayrollLeaveEarning). Rows have the same fields as Shift Earnings, so
// the Shift request helper and row adapter are reused.
const BASE = "payroll/earning/leave/";

export const fromLeaveApi = fromShiftApi;

// Leave Type options (GET payroll/earning/leave/earning/types/): a plain list of
// names, e.g. ["Leave", "Paid Leave", ...]. Same shape as the Shift Earnings
// day types; objects with a name or label are accepted too.
export const loadLeaveEarningTypes = async (domain, signal) => {
  const data = await shiftRequest(domain, `${BASE}earning/types/`, { signal });
  const list = Array.isArray(data) ? data : data?.results;
  if (!Array.isArray(list)) throw new Error("The server returned an invalid leave type list.");
  return [...new Set(list.map((item) => (typeof item === "string" ? item : item?.name ?? item?.label ?? "")).filter(Boolean))];
};

export const toLeavePayload = (form, { creating = false } = {}) => ({
  ...(creating ? { employee_id: form.employee_code } : {}),
  date: form.date,
  earning_type: form.earning_type,
  ...(creating && !form.shift_name ? {} : { shift: form.shift_name || null }),
  basis: form.basis,
  quantity: Number(form.quantity),
  rate: Number(form.rate),
  factor: Number(form.payment_factor),
  ...(form.pay_schedule_id === "" ? (creating ? {} : { pay_schedule_id: null }) : { pay_schedule_id: Number(form.pay_schedule_id) }),
  ...(!creating ? { status: form.status } : {}),
});

export const listLeaveEarnings = async (domain, companyId, period, signal) => {
  const params = new URLSearchParams({ company_id: companyId, start_date: period.start, end_date: period.end });
  const data = await shiftRequest(domain, `${BASE}earning_details/?${params}`, { signal });
  if (!Array.isArray(data)) throw new Error("The server returned an invalid leave earnings list.");
  return data.filter((row) => !row.isdeleted).map(fromLeaveApi);
};

// Edits send only the fields that changed, so a status or payroll cycle
// change never resends (and recalculates) the amount.
export const saveLeaveEarning = async (domain, form, id, signal, initial) => {
  const payload = toLeavePayload(form, { creating: id == null });
  const before = initial ? toLeavePayload(initial) : null;
  const body = before ? Object.fromEntries(Object.entries(payload).filter(([key, value]) => value !== before[key])) : payload;
  const row = await shiftRequest(domain, `${BASE}earning/${id == null ? "" : `${id}/`}`, {
    method: id == null ? "POST" : "PUT", body, signal,
  });
  if (row?.id == null) throw new Error("The server returned an invalid leave earning.");
  return fromLeaveApi(row);
};

export const deleteLeaveEarning = (domain, id, signal) => shiftRequest(domain, `${BASE}earning/${id}/`, { method: "DELETE", signal });

export const bulkLeaveEarnings = async (domain, ids, changes, signal) => {
  const data = await shiftRequest(domain, `${BASE}earning/bulk-action/`, { method: "PUT", body: { ids, ...changes }, signal });
  if (!Array.isArray(data?.updated) || !Array.isArray(data?.errors)) throw new Error("The server returned an invalid bulk action result. Refresh the list before trying again.");
  return data;
};

// records: [{ employee_id, date, earning_type, basis, quantity, rate, factor?, amount?, shift? }]
// Returns { total, success, failed, rows: [{ employee_id, date, status, message }] }.
// The server answers 400 when no row was saved, but still with the per-row
// results, so that body is returned instead of thrown.
export const importLeaveEarnings = async (domain, records, paySchedule, signal) => {
  const response = await fetch(`${domain.replace(/\/$/, "")}/${BASE}import/`, {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json", Authorization: `Token ${localStorage.getItem("token")}` },
    body: JSON.stringify({ records, ...(paySchedule ? { pay_schedule_id: Number(paySchedule) } : {}) }),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("Your session has expired. Please sign in again.");
  }
  const data = await response.json().catch(() => null);
  if (Array.isArray(data?.rows)) return data;
  throw new Error(data?.error || `Import failed (${response.status}). Please try again.`);
};

// Same body as the import; checks every record on the server without saving.
// Returns { total, valid, failed, rows: [{ index, status: "valid" | "failed",
// message, amount, pay_schedule_name }] } where index is the record's
// position in `records`.
export const validateLeaveImport = async (domain, records, paySchedule, signal) => {
  const data = await shiftRequest(domain, `${BASE}import/validate/`, {
    method: "POST",
    signal,
    body: { records, ...(paySchedule ? { pay_schedule_id: Number(paySchedule) } : {}) },
  });
  if (!Array.isArray(data?.rows)) throw new Error("The server returned an invalid validation result.");
  return data;
};
